import { themes } from '@weenat/theme';
import { useIntl } from '@weenat/wintl';
import groupBy from 'lodash-es/groupBy';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import moment from 'moment-timezone';
import * as React from 'react';
import { useClient } from '../ClientProvider';
import { irrigation } from '../core/dss';
import { addToWaterBalanceResult, getArvalisStageName, useArvalisIds } from '../core/dss/waterBalance';
import { getPeriodWithoutForecast } from '../core/history';
import TimeSteps from '../enums/TimeSteps';
import { mergeQueryState } from '../mergeState';
import { AggregationType } from '../resources/measurements.type';
import { fromArvalisDateString } from '../resources/waterBalanceArvalis';
import useQuery from '../useQuery';
import { useCorrectedRainfalls, useCropTracking } from './useCropTracking';
const EMPTY_STAGES = [];
const EMPTY_WATER_INTAKE = [];
const EMPTY_RESULTS = [];
const NOW = Math.round(new Date().getTime() / 1000);
const EMPTY_AXIS_STAGES = [];
/** To check between raw irrigation report or a raw rainfalls correction report */
export const isMeasurementReport = (rep) => {
    return 'type' in rep;
};
/** To check between an two watering report */
export const isIrrigationWateringReport = (rep) => {
    return rep.type === 'irrigation';
};
export function measurementReportsToWateringReports(initialReports) {
    return initialReports.reduce((acc, report) => {
        if (!isNil(report.value)) {
            const finalId = isMeasurementReport(report)
                ? report.id
                : !isNil(report.reportId)
                    ? report.reportId
                    : undefined;
            const finalType = isMeasurementReport(report)
                ? 'irrigation'
                : 'rainfalls';
            if (!isNil(finalId))
                acc.push({
                    ...report,
                    id: finalId,
                    type: finalType
                });
        }
        return acc;
    }, []);
}
const lightTheme = themes.light;
export const waterBalanceDash = 4;
export const chartLegendStyle = {
    irrigation: { color: lightTheme.colors.metrics.IRR['500'] },
    WB: { color: lightTheme.colors.metrics.HDEF['500'] },
    WB_forecast: { color: lightTheme.colors.metrics.HDEF['500'], strokeDasharray: waterBalanceDash },
    rain: {},
    threshold: {},
    stage: {},
    drainage: { color: lightTheme.colors.metrics.DRN['500'] }
};
const EMPTY_WATER_BALANCE_DATUM = [];
function computeAsStringField(datum) {
    return {
        ...datum,
        asString: JSON.stringify(datum)
    };
}
/**
 * as this chart is relative this function compute relative value from absolute one
 */
function toRelativeValue(value, relativeScale, y0 = 0) {
    return value !== null ? value * relativeScale + y0 : null;
}
/**
 * in this function we compute a relative value which is
 * current threshold value minus previous threshold this way each threshold is independent
 * and because of that we can stack them
 */
const toThresholds = ({ relativeScale, color, data, getPreviousValue, stagesAxis }) => {
    return {
        color,
        data: Object.entries(data).reduce((acc, [timestamp, value], idx, values) => {
            const thresholdDate = new Date(parseInt(timestamp, 10) * 1000);
            const stageDate = stagesAxis.find((stage) => stage.value?.getDate() === thresholdDate.getDate());
            const isFirstValue = idx === 0;
            const isLastValue = idx === values.length - 1;
            // restrict threshold values to stages but accept values for start(idx===0) and end of X domain
            // this way threshold could go from first to last values even if last values doesn't match stage
            if (!isNil(stageDate) || isFirstValue || isLastValue) {
                const finalValue = value === null ? null : value - (getPreviousValue(timestamp) || 0);
                acc.push(computeAsStringField({
                    x: thresholdDate,
                    y: toRelativeValue(finalValue, relativeScale),
                    value,
                    type: 'threshold'
                }));
            }
            return acc;
        }, [])
    };
};
/**
 * There is a specificity for irrigations
 * because Victory impose us to always have values we need to fill the gaps
 * between the water results data and the irrigations
 */
const irrigationToChartPoint = ({ values, relativeScale, yMin, label, waterBalancePoints }) => {
    return waterBalancePoints.map((point) => {
        const matchingReportAtDate = values.find((report) => moment(point.x).isSame(new Date(parseInt(report.timestamp, 10) * 1000), 'day'));
        const matchingReportAtDateValueAsNumber = typeof matchingReportAtDate?.value === 'number'
            ? matchingReportAtDate.value
            : typeof matchingReportAtDate?.value === 'string'
                ? parseFloat(matchingReportAtDate.value)
                : matchingReportAtDate?.value?.dose ?? null;
        return computeAsStringField({
            l: label,
            x: !isNil(matchingReportAtDate)
                ? new Date(parseInt(matchingReportAtDate.timestamp, 10) * 1000)
                : point.x,
            y: !isNil(matchingReportAtDate)
                ? toRelativeValue(matchingReportAtDateValueAsNumber, relativeScale, yMin)
                : null,
            y0: yMin,
            value: matchingReportAtDateValueAsNumber,
            type: 'irrigation'
        });
    });
};
const rainFallsToChartPoint = ({ yMin, relativeScale, values, label, rainfallsColorScale }) => {
    const scale = rainfallsColorScale(values);
    return !isNil(values)
        ? values.reduce((acc, rainfalls) => {
            if (!isNil(rainfalls.value)) {
                acc.push(computeAsStringField({
                    l: label,
                    x: new Date(rainfalls.timestamp * 1000),
                    y: toRelativeValue(rainfalls.value || null, relativeScale, yMin),
                    y0: yMin,
                    value: rainfalls.value ?? null,
                    type: 'rain',
                    color: scale(rainfalls.value)
                }));
            }
            return acc;
        }, [])
        : EMPTY_WATER_BALANCE_DATUM;
};
const drainageToChartPoint = ({ values, relativeScale, yMin, label }) => {
    return !isNil(values)
        ? values.reduce((acc, { date, value }) => {
            if (!isNil(value)) {
                acc.push(computeAsStringField({
                    l: label,
                    x: date,
                    y: toRelativeValue(value, relativeScale, yMin),
                    y0: yMin,
                    value,
                    type: 'drainage'
                }));
            }
            return acc;
        }, [])
        : EMPTY_WATER_BALANCE_DATUM;
};
const waterBalanceToChartLine = ({ values, relativeScale, label }) => {
    return (values &&
        Object.entries(values).reduce((acc, [timestamp, value]) => [
            ...acc,
            computeAsStringField({
                x: new Date(parseInt(timestamp, 10) * 1000),
                y: toRelativeValue(value, relativeScale),
                value,
                l: label,
                type: 'WB'
            })
        ], []));
};
const EMPTY_WB_DATA = {};
export function useGetWeenatWaterBalancePeriod({ plot, isEnabled }) {
    const { waterBalanceResultRequest } = useCropTracking({ plot, isEnabled });
    const wbResult = waterBalanceResultRequest.data ?? EMPTY_WB_DATA;
    const wbData = !isEmpty(wbResult)
        ? wbResult.data
        : EMPTY_WB_DATA;
    const HYDEntries = Object.values(wbData).filter((elt) => elt !== undefined);
    const HYDTimestamps = HYDEntries.flatMap((elt) => {
        return Object.keys(elt);
    }).map((timestamp) => parseInt(timestamp, 10));
    const since = !isEmpty(HYDTimestamps) ? Math.min(...HYDTimestamps) : 0;
    const before = !isEmpty(HYDTimestamps) ? Math.max(...HYDTimestamps) : 0;
    const period = !isNil(wbResult)
        ? { start: moment.unix(since).toDate(), end: moment.unix(before).toDate() }
        : undefined;
    return { periodRequest: waterBalanceResultRequest, period };
}
const WATER_BALANCE_TIME_STEP = TimeSteps.perDays;
export function useWeenatWaterBalanceWatering({ plot, since, before, isEnabled, isIrrigationFromReportEnabled, logError }) {
    const client = useClient();
    const irrigationRequest = useQuery(client.measurementsReports.getAllPages({
        plotId: plot.id,
        metrics: ['IRR'],
        before: `${before}`,
        since: `${since}`
    }), {
        enabled: since !== 0 &&
            before !== 0 &&
            since !== before &&
            isEnabled &&
            isIrrigationFromReportEnabled
    });
    // back is giving us forecast when we query this endpoint
    // but water balance computation is not based on forecast so we limit period
    const rainfallPeriod = since !== 0 && before !== 0 && since !== before
        ? getPeriodWithoutForecast({
            period: { since, before },
            timezone: plot.timezone,
            timeStep: WATER_BALANCE_TIME_STEP
        })
        : undefined;
    const rainfallsRequest = useCorrectedRainfalls({
        plotId: plot.id,
        period: rainfallPeriod ? { ...rainfallPeriod, span: WATER_BALANCE_TIME_STEP } : undefined,
        logError,
        isEnabled
    });
    return {
        irrigationRequest,
        rainfallsRequest,
        irrigations: irrigationRequest.data,
        rainfalls: rainfallsRequest.rainfalls
    };
}
export function useWaterBalanceData({ plot, logError, isEnabled = true, isIrrigationFromReportEnabled = true }) {
    const { stages, waterBalanceResultRequest, lastCrop } = useCropTracking({ plot, isEnabled });
    const HYDEntries = Object.values(waterBalanceResultRequest.data?.data || {}).filter((elt) => elt !== undefined);
    const HYDTimestamps = HYDEntries.flatMap((elt) => {
        return Object.keys(elt);
    }).map((timestamp) => parseInt(timestamp, 10));
    const since = !isEmpty(HYDTimestamps) ? Math.min(...HYDTimestamps) : 0;
    const before = !isEmpty(HYDTimestamps) ? Math.max(...HYDTimestamps) : 0;
    const { irrigations, rainfalls, irrigationRequest, rainfallsRequest } = useWeenatWaterBalanceWatering({
        plot,
        isEnabled,
        since,
        before,
        logError,
        isIrrigationFromReportEnabled
    });
    const requestsState = mergeQueryState(irrigationRequest, waterBalanceResultRequest, rainfallsRequest);
    const refetch = React.useCallback(() => {
        irrigationRequest.refetch(), waterBalanceResultRequest.refetch(), rainfallsRequest.refetch();
    }, [irrigationRequest, waterBalanceResultRequest, rainfallsRequest]);
    const { isLoading, isFetching } = requestsState;
    return {
        before,
        HYDEntries,
        irrigations,
        isFetching,
        isLoading,
        lastCrop,
        rainfalls,
        refetch,
        since,
        stages,
        waterBalanceResultRequest
    };
}
const MOCKED_USER_ID = 1;
export const wbArvalisPeriodIds = ['last_14_days', 'last_30_days', 'current_season'];
function getDateRangeFromArvalisPeriodId(id, timezone) {
    const today = moment(new Date(Date.now())).tz(timezone).startOf('day');
    let period;
    switch (id) {
        /** Special Arvalis case not 30 days, but 30 days plus 6 days of forecasts */
        case 'last_30_days':
            period = {
                start: today.clone().subtract(1, 'month').startOf('day').toDate(),
                end: today.clone().add(6, 'day').startOf('day').toDate()
            };
            break;
        /** Special Arvalis case not 14 days, but 14 days plus 6 days of forecasts */
        case 'last_14_days':
            period = {
                start: today.clone().subtract(14, 'day').startOf('day').toDate(),
                end: today.clone().add(6, 'day').startOf('day').toDate()
            };
            break;
        default:
            period = undefined;
            break;
    }
    return period;
}
export function useArvalisWaterBalancePeriodOptions(t) {
    return React.useMemo(() => wbArvalisPeriodIds.map((p) => ({
        value: p,
        label: t(`periods.${p}`, { capitalize: true })
    })), [t]);
}
/**
 * Fetch the water balance result to get the full period of the water balance
 */
export function useGetArvalisWaterBalancePeriod({ plot, isEnabled = true }) {
    const client = useClient();
    const { arvalisPlotId, clientId } = useArvalisIds(plot, isEnabled);
    const periodRequest = useQuery(client.waterBalanceArvalis.getResult({
        clientId: clientId,
        plotId: arvalisPlotId
    }), { enabled: !isNil(clientId) && !isNil(arvalisPlotId) && isEnabled });
    const period = !isNil(periodRequest.data)
        ? {
            start: periodRequest.data.waterBalanceStart,
            end: periodRequest.data.waterBalanceEnd
        }
        : undefined;
    return { periodRequest, period };
}
export const useArvalisWatering = ({ plot, isEnabled = true }) => {
    const client = useClient();
    const { clientId, orgRequest, arvalisPlotId } = useArvalisIds(plot, isEnabled);
    const areRequestsEnabled = !isNil(plot.id) && !isNil(clientId) && isEnabled;
    const irrigationsRequest = useQuery(client.waterBalanceArvalis.getIrrigations({
        clientId: clientId,
        plotId: arvalisPlotId
    }), { enabled: areRequestsEnabled });
    const rainfallsRequest = useQuery(client.waterBalanceArvalis.getRainfalls({
        clientId: clientId,
        plotId: arvalisPlotId
    }), { enabled: areRequestsEnabled });
    const { isFetching, isLoading } = mergeQueryState(rainfallsRequest, orgRequest, irrigationsRequest);
    const rainfalls = rainfallsRequest.data ?? EMPTY_WATER_INTAKE;
    const irrigations = irrigationsRequest.data ?? EMPTY_WATER_INTAKE;
    const filteredIrrigations = React.useMemo(() => irrigations.reduce((acc, entry) => {
        if (entry.dose !== 0) {
            const date = moment(entry.date, 'DD/MM/YYYY').unix();
            acc.push({
                id: date,
                plotId: plot.id,
                timestamp: `${date}`,
                type: 'irrigation',
                // Mocking a weenat object to harmonize arvalis' and weenat's irrigations
                userId: MOCKED_USER_ID,
                value: entry.dose
            });
        }
        return acc;
    }, []), [irrigations, plot.id]);
    const filteredRainfalls = React.useMemo(() => rainfalls.reduce((acc, entry, _, all) => {
        const duplicatedReports = all.filter((crr) => crr.date === entry.date);
        const isUnique = duplicatedReports.length <= 1;
        const isOverride = entry.type === 'manual';
        const shouldBeAdded = isUnique || (!isUnique && isOverride);
        if (shouldBeAdded) {
            let originalReport = undefined;
            if (!isUnique) {
                originalReport = duplicatedReports.find((crr) => crr.type !== 'manual');
            }
            const correctedDose = entry?.dose;
            const originalValue = !isNil(originalReport) ? originalReport.dose : entry?.dose;
            const value = (correctedDose === 0 && !isOverride) || correctedDose === undefined
                ? null
                : correctedDose;
            const dateInMs = fromArvalisDateString(entry.date).getTime();
            acc.push({
                isOverride,
                originalValue: originalValue === undefined ? null : originalValue,
                reportId: dateInMs,
                timestamp: Math.round(dateInMs / 1000),
                // Mocking a weenat object to harmonize arvalis' and weenat's irrigations
                userId: MOCKED_USER_ID,
                value
            });
        }
        return acc;
    }, []), [rainfalls]);
    return {
        irrigations: filteredIrrigations,
        irrigationsRequest,
        isFetching: !!isFetching,
        isLoading,
        rainfalls: filteredRainfalls,
        rainfallsRequest
    };
};
const ARRAY_OF_UNDEFINED = [undefined];
export const useWaterBalanceDataArvalis = ({ cropType, displayedPeriod, isEnabled = true, plot }) => {
    const client = useClient();
    const { clientId, orgRequest, arvalisPlotId } = useArvalisIds(plot, isEnabled);
    const { timezone } = plot;
    const areRequestsEnabled = !isNil(plot.id) && !isNil(clientId) && isEnabled;
    const now = new Date(Date.now());
    const period = !isNil(displayedPeriod)
        ? getDateRangeFromArvalisPeriodId(displayedPeriod, timezone)
        : undefined;
    const startDate = !isNil(period) ? period.start : undefined;
    const endDate = !isNil(period) ? period.end : undefined;
    const request = useQuery(client.waterBalanceArvalis.getResult({
        clientId: clientId,
        plotId: arvalisPlotId,
        start: startDate,
        end: endDate
    }), { enabled: areRequestsEnabled });
    const irrigationPeriodsRequest = useQuery(client.waterBalanceArvalis.getIrrigationPeriods({
        clientId: clientId,
        cropType: cropType
    }), { enabled: areRequestsEnabled && !isNil(cropType) });
    const { rainfallsRequest, irrigationsRequest, rainfalls, irrigations } = useArvalisWatering({
        plot,
        isEnabled
    });
    const stagesRequest = useQuery(client.waterBalanceArvalis.getStages({ clientId: clientId, plotId: arvalisPlotId }), { enabled: areRequestsEnabled });
    const { isFetching, isLoading } = mergeQueryState(request, rainfallsRequest, stagesRequest, orgRequest, irrigationsRequest, irrigationPeriodsRequest);
    const stages = stagesRequest.data ?? EMPTY_STAGES;
    const waterBalance = request.data?.waterBalance ?? EMPTY_RESULTS;
    const irrigationPeriods = irrigationPeriodsRequest.data;
    /** will default on season start if not provided */
    const startDateDisplayed = !isNil(startDate)
        ? startDate
        : !isNil(request.data) && !isNil(request.data.waterBalanceStart)
            ? request.data.waterBalanceStart
            : undefined;
    const since = !isNil(startDateDisplayed)
        ? Math.round(startDateDisplayed.getTime() / 1000)
        : undefined;
    /** will default on season end if not provided */
    const endDateDisplayed = !isNil(endDate)
        ? endDate
        : !isNil(request.data) && !isNil(request.data.waterBalanceEnd)
            ? request.data.waterBalanceEnd
            : undefined;
    const before = !isNil(endDateDisplayed)
        ? Math.round(endDateDisplayed.getTime() / 1000)
        : undefined;
    const refetch = React.useCallback(() => {
        request.refetch();
        rainfallsRequest.refetch();
    }, [request, rainfallsRequest]);
    const [lastStage] = !isEmpty(stages) ? stages.slice(-1) : ARRAY_OF_UNDEFINED;
    const isOver = !isNil(lastStage) && !isNil(lastStage.date) ? now.getTime() >= lastStage.date.getTime() : false;
    return {
        before,
        irrigationPeriods,
        irrigations,
        isFetching: !!isFetching,
        isLoading,
        isOver,
        rainfalls,
        refetch,
        since,
        stages,
        status: request.data?.status,
        waterBalance
    };
};
const extractPeriodFromWBArvalisIrrigationPeriods = ({ irrigationPeriods, stages }) => {
    const sensitivityPeriod = irrigationPeriods.sensitivityPeriod;
    const matchingStartingStage = stages.find((st) => !isNil(st.code) && !isNil(sensitivityPeriod) && st.code === sensitivityPeriod.start.stage);
    const matchingEndingStage = stages.find((st) => !isNil(st.code) && !isNil(sensitivityPeriod) && st.code === sensitivityPeriod.end.stage);
    const startingStageDate = !isNil(matchingStartingStage)
        ? !isNil(matchingStartingStage.date)
            ? matchingStartingStage.date instanceof Date
                ? matchingStartingStage.date
                : new Date(parseInt(matchingStartingStage.date, 10) * 1000)
            : !isNil(matchingStartingStage.forecastedDate)
                ? new Date(parseInt(matchingStartingStage.forecastedDate, 10) * 1000)
                : null
        : null;
    const startingDelta = sensitivityPeriod?.start.delta;
    const start = !isNil(startingStageDate)
        ? !isNil(startingDelta)
            ? moment(startingStageDate).add(startingDelta, 'days').toDate()
            : startingStageDate
        : null;
    const endingStageDate = !isNil(matchingEndingStage)
        ? !isNil(matchingEndingStage.date)
            ? matchingEndingStage.date instanceof Date
                ? matchingEndingStage.date
                : new Date(parseInt(matchingEndingStage.date, 10) * 1000)
            : !isNil(matchingEndingStage.forecastedDate)
                ? new Date(parseInt(matchingEndingStage.forecastedDate, 10) * 1000)
                : null
        : null;
    const endingDelta = sensitivityPeriod?.end.delta;
    const end = !isNil(endingStageDate)
        ? !isNil(endingDelta)
            ? moment(endingStageDate).add(endingDelta, 'days').toDate()
            : endingStageDate
        : null;
    return { start, end };
};
const useTransformWaterBalanceToChartData = ({ before, drainageResults, irrigationPeriods, irrigationResults, isFetching, isLoading, lastCrop, minY, rainfallsColorScale, rainfallsResults, since, stages, staleWhileRefetch = true, status, waterBalanceResult }) => {
    const { t } = useIntl();
    const domain = React.useMemo(() => {
        return since !== before
            ? {
                x: [new Date(since * 1000), new Date(before * 1000)],
                y: [-1, 0]
            }
            : undefined;
    }, [since, before]);
    const fillColorFromDatum = React.useMemo(() => {
        return ({ datum }) => {
            return datum.color || 'black';
        };
    }, []);
    /**
     * data transformation for chart
     */
    const computedChartData = React.useMemo(() => {
        let result = {
            afterNowWaterBalance: undefined,
            beforeNowWaterBalance: undefined,
            domain: undefined,
            drainages: undefined,
            fillColorFromDatum: () => '',
            HYDRelativeScale: 1,
            irrigations: undefined,
            isLoading: false,
            lastCrop: undefined,
            rainFalls: undefined,
            sensitivityPeriod: undefined,
            stagesAxis: undefined,
            stagesLine: undefined,
            thresholds: undefined,
            isEmpty: true
        };
        const shouldRecompute = staleWhileRefetch ? !isLoading : !isLoading && !isFetching;
        if (shouldRecompute) {
            const now = Date.now();
            const HYDRelativeScale = minY ? Math.abs(1 / minY) : 1;
            const waterBalanceData = waterBalanceToChartLine({
                values: waterBalanceResult?.HYD,
                relativeScale: HYDRelativeScale,
                label: t('metrics.waterDeficit', { capitalize: true })
            }) ?? EMPTY_WATER_BALANCE_DATUM;
            const { false: beforeNowWaterBalance, true: after } = groupBy(waterBalanceData, ({ x }) => (isNil(x) ? 0 : x.getTime()) > now);
            const lastNonNullValue = !isNil(after) && !isEmpty(after)
                ? [...after].reverse().find((datum) => datum.value !== null)
                : undefined;
            const afterNowWaterBalance = after?.reduce((acc, datum) => {
                if (lastNonNullValue === undefined ||
                    datum.x === null ||
                    lastNonNullValue.x === null ||
                    datum.x.getTime() <= lastNonNullValue.x.getTime()) {
                    acc.push(computeAsStringField({
                        ...datum,
                        type: 'WB_forecast'
                    }));
                }
                return acc;
            }, []);
            const sensitivityPeriod = !isNil(irrigationPeriods) && !isNil(stages)
                ? extractPeriodFromWBArvalisIrrigationPeriods({ irrigationPeriods, stages })
                : undefined;
            const stagesAxis = !isNil(stages)
                ? stages.map((stage) => ({
                    value: (stage.date !== null
                        ? stage.date instanceof Date
                            ? stage.date
                            : new Date(parseInt(stage.date, 10) * 1000)
                        : null) ||
                        (stage.forecastedDate !== undefined
                            ? new Date(parseInt(stage.forecastedDate, 10) * 1000)
                            : null),
                    name: stage.name
                }))
                : EMPTY_AXIS_STAGES;
            const reverseStagesAxis = [...stagesAxis].reverse();
            const thresholdLvl1 = !isNil(waterBalanceResult) && !isNil(waterBalanceResult.HYD_LVL_1)
                ? toThresholds({
                    data: waterBalanceResult.HYD_LVL_1,
                    color: irrigation.thresholdColorScale('comfort'),
                    relativeScale: HYDRelativeScale,
                    getPreviousValue: () => 0,
                    stagesAxis
                })
                : {};
            const thresholdLvl2 = !isNil(waterBalanceResult) &&
                !isNil(waterBalanceResult.HYD_LVL_1) &&
                !isNil(waterBalanceResult.HYD_LVL_2)
                ? toThresholds({
                    data: waterBalanceResult.HYD_LVL_2,
                    color: irrigation.thresholdColorScale('vigilance'),
                    relativeScale: HYDRelativeScale,
                    getPreviousValue: (ts) => waterBalanceResult.HYD_LVL_1[ts] ?? null,
                    stagesAxis
                })
                : {};
            const thresholdLvl3 = !isNil(waterBalanceResult) &&
                !isNil(waterBalanceResult.DEFI_MAX) &&
                !isNil(waterBalanceResult.HYD_LVL_2)
                ? toThresholds({
                    data: waterBalanceResult.DEFI_MAX,
                    color: irrigation.thresholdColorScale('stress'),
                    relativeScale: HYDRelativeScale,
                    getPreviousValue: (ts) => waterBalanceResult.HYD_LVL_2[ts] ?? null,
                    stagesAxis
                })
                : {};
            const thresholds = [thresholdLvl1, thresholdLvl2, thresholdLvl3];
            const waterIntakeRelativeScale = HYDRelativeScale;
            const irrigations = !isNil(irrigationResults)
                ? irrigationToChartPoint({
                    label: t('metrics.irrigation', { capitalize: true }),
                    values: irrigationResults,
                    waterBalancePoints: waterBalanceData,
                    relativeScale: waterIntakeRelativeScale,
                    yMin: minY * HYDRelativeScale
                })
                : EMPTY_WATER_BALANCE_DATUM;
            let rainFalls = EMPTY_WATER_BALANCE_DATUM;
            rainFalls = rainFallsToChartPoint({
                label: t('metrics.RR', { capitalize: true }),
                values: rainfallsResults,
                relativeScale: waterIntakeRelativeScale,
                yMin: minY * HYDRelativeScale,
                rainfallsColorScale
            });
            const drainages = !isNil(drainageResults)
                ? drainageToChartPoint({
                    label: t('metrics.DRN', { capitalize: true }),
                    values: drainageResults,
                    relativeScale: waterIntakeRelativeScale,
                    yMin: minY * HYDRelativeScale
                })
                : EMPTY_WATER_BALANCE_DATUM;
            const stagesLine = !isNil(thresholdLvl1.data)
                ? thresholdLvl1.data.map(({ x }) => {
                    const currentStage = reverseStagesAxis.find((stage) => (stage.value ?? Number.MAX_SAFE_INTEGER) <= (isNil(x) ? 0 : x))?.name;
                    return computeAsStringField({
                        x,
                        y: -10,
                        l: isEmpty(currentStage) || isNil(currentStage)
                            ? ''
                            : t('dss.cropTracking.status.stage', { stage: currentStage }),
                        value: 100,
                        type: 'stage'
                    });
                })
                : EMPTY_WATER_BALANCE_DATUM;
            const isDataEmpty = isEmpty(beforeNowWaterBalance) &&
                isEmpty(afterNowWaterBalance) &&
                (isEmpty(thresholds) ||
                    isEmpty(thresholds?.reduce((acc, threshold) => {
                        return acc.concat(threshold.data);
                    }, [])));
            result = {
                afterNowWaterBalance,
                beforeNowWaterBalance,
                domain,
                drainages,
                fillColorFromDatum,
                HYDRelativeScale,
                irrigations,
                isLoading: staleWhileRefetch ? isLoading : isLoading || isFetching,
                lastCrop,
                rainFalls,
                sensitivityPeriod,
                stagesAxis,
                stagesLine,
                status,
                thresholds,
                isEmpty: isDataEmpty
            };
        }
        return result;
    }, [
        staleWhileRefetch,
        isLoading,
        isFetching,
        minY,
        waterBalanceResult,
        t,
        irrigationPeriods,
        stages,
        irrigationResults,
        rainfallsResults,
        rainfallsColorScale,
        drainageResults,
        domain,
        fillColorFromDatum,
        lastCrop,
        status
    ]);
    return computedChartData;
};
export const useWaterBalanceChartData = ({ plot, rainfallsColorScale, logError }) => {
    /**
     * data fetching part
     */
    const { rainfalls, before, since, waterBalanceResultRequest, HYDEntries, stages, isLoading, isFetching, lastCrop } = useWaterBalanceData({ plot, logError, isIrrigationFromReportEnabled: false });
    const client = useClient();
    const irrigationRequest = useQuery(client.measurements.plots.histories.get1_1({
        metrics: ['IRR'],
        before,
        since,
        plotId: plot.id,
        span: TimeSteps.perDays
    }));
    const HYDValues = HYDEntries.flatMap((elt) => {
        return Object.values(elt);
    }).filter((elt) => elt !== null);
    const minY = Math.min(...HYDValues);
    return useTransformWaterBalanceToChartData({
        since,
        before,
        isLoading: isLoading || irrigationRequest.isLoading,
        minY,
        waterBalanceResult: waterBalanceResultRequest.data?.data,
        stages,
        irrigationResults: irrigationRequest.data?.values?.['IRR']?.[AggregationType.sum]?.reduce((acc, x) => {
            if (x.value !== null) {
                acc.push({
                    id: x.timestamp,
                    plotId: plot.id,
                    timestamp: `${x.timestamp}`,
                    type: 'irrigation',
                    userId: 0,
                    value: x.value
                });
            }
            return acc;
        }, []),
        rainfallsResults: rainfalls,
        rainfallsColorScale,
        isFetching: !!(isFetching || irrigationRequest.isFetching),
        lastCrop
    });
};
export const useWaterBalanceChartDataArvalis = ({ rainfallsColorScale, plot, cropType, staleWhileRefetch = true, displayedPeriod }) => {
    const { irrigations, rainfalls, waterBalance, stages, isLoading, isFetching, irrigationPeriods, since: sinceFromHook, before: beforeFromHook, status } = useWaterBalanceDataArvalis({ plot, cropType, displayedPeriod });
    const since = isNil(waterBalance) || isNil(sinceFromHook) ? NOW : sinceFromHook;
    const before = isEmpty(waterBalance) || isNil(waterBalance) || isNil(beforeFromHook) ? NOW : beforeFromHook;
    const initial = {
        DEFI_MAX: {},
        HYD: {},
        HYD_LVL_1: {},
        HYD_LVL_2: {}
    };
    const waterBalanceResult = !isNil(waterBalance)
        ? waterBalance.reduce((acc, entry) => {
            addToWaterBalanceResult(entry, acc);
            return acc;
        }, initial)
        : initial;
    const drainageResults = !isNil(waterBalance)
        ? waterBalance.map(({ date, profileDrainage }) => {
            return { date, value: profileDrainage ?? null };
        })
        : [];
    const HYDValues = Object.values(waterBalanceResult)
        .flatMap((elt) => {
        return Object.values(elt);
    })
        .filter((elt) => elt !== null);
    const minY = Math.min(...HYDValues);
    const finalStages = stages.map((wbArvalisStage) => ({
        code: wbArvalisStage.code,
        date: wbArvalisStage.date,
        name: getArvalisStageName({ code: wbArvalisStage.code, cropType }) || ''
    }));
    return useTransformWaterBalanceToChartData({
        since,
        before,
        isLoading,
        minY,
        waterBalanceResult,
        stages: finalStages,
        irrigationResults: irrigations,
        rainfallsResults: rainfalls,
        drainageResults,
        rainfallsColorScale,
        isFetching,
        staleWhileRefetch,
        irrigationPeriods,
        status
    });
};
export function useGetWaterBalancePeriod({ plot, isEnabled = true, isArvalis = false }) {
    const wbData = useGetWeenatWaterBalancePeriod({
        plot,
        isEnabled: !isArvalis && isEnabled
    });
    const arvalisWBData = useGetArvalisWaterBalancePeriod({
        plot,
        isEnabled: isArvalis && isEnabled
    });
    return isArvalis ? arvalisWBData : wbData;
}
